/**
 * Website Institucional do Vende Aí
 *
 * Página inicial pública (rota "/") que apresenta o sistema, as dores que ele
 * resolve, os segmentos atendidos, os planos, o modelo de mensalidade e a
 * implantação, com cadastro self-service (teste grátis) e formulário de
 * contato comercial. Cada segmento tem página própria em /para/:segmento.
 *
 * Estrutura baseada na pesquisa de concorrentes (docs/PESQUISA_CONCORRENTES.md).
 *
 * @author Equipe VendeAi
 * @since 1.1.0
 */

import { useCallback, useState } from 'react';
import { useAuth } from '@hooks/useAuth';
import { FALLBACK_PLANS } from './content';
import { HOME_TITLE, HOME_DESCRIPTION } from './seo';
import { usePageMeta } from './usePageMeta';
import { usePlatformData } from './usePlatformData';
import { useHashScroll } from './useHashScroll';
import { SiteHeader } from './components/SiteHeader';
import { Hero } from './components/Hero';
import { PainPoints } from './components/PainPoints';
import { Features, BeforeAfter } from './components/Features';
import { Segments, SavingsCalculator } from './components/SegmentsAndSavings';
import { Onboarding, Pricing, BillingModel, Comparison } from './components/Pricing';
import { Testimonials } from './components/Testimonials';
import {
  Faq,
  ContactSection,
  FinalCta,
  SiteFooter,
  WhatsAppFloat,
  MobileStickyCta,
} from './components/FaqContact';
import './website.css';

export function Website() {
  const { isAuthenticated } = useAuth();
  const { plans, plansLoading, platform, testimonials, trialDays } = usePlatformData();
  const [selectedPlan, setSelectedPlan] = useState('');

  usePageMeta(HOME_TITLE, HOME_DESCRIPTION, '/');
  useHashScroll();

  const handleTalkToSales = useCallback((planSlug: string) => {
    setSelectedPlan(planSlug);
    document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div className="vw-root min-h-screen">
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-slate-900"
      >
        Pular para o conteúdo
      </a>
      <SiteHeader isAuthenticated={isAuthenticated} />
      <main id="conteudo">
        <Hero trialDays={trialDays} />
        <PainPoints />
        <Features />
        <BeforeAfter />
        <SavingsCalculator plans={plans.length > 0 ? plans : FALLBACK_PLANS} />
        <Segments />
        <Testimonials items={testimonials} />
        <Onboarding />
        <Pricing plans={plans} loading={plansLoading} onTalkToSales={handleTalkToSales} />
        <BillingModel trialDays={trialDays} />
        <Comparison />
        <Faq />
        <ContactSection plans={plans} selectedPlan={selectedPlan} platform={platform} />
        <FinalCta trialDays={trialDays} />
      </main>
      <SiteFooter platform={platform} />
      <WhatsAppFloat number={platform?.supportWhatsApp} />
      <MobileStickyCta label={`Testar grátis por ${trialDays} dias`} />
    </div>
  );
}
